/**
 * Slip a mission in immediately before another.
 *
 * The new mission inherits the source's incoming edges. The source then
 * depends only on the new mission. Outgoing edges of the source stay put.
 */
import { hasAnyConditions } from '../../shared/ConditionsDropdown/utils/conditionHelpers';
import { defaultWaitTitle } from '../../../../utils/waitFor';
import { copyIncomingDependencies, isParallelMissionSource } from './parallelMission';

const BEFORE_OFFSET_Y = 72;

function singleDependencyOn(taskId) {
  return {
    version: 2,
    blocks: [{
      conditions: [{ taskId, branch: null }],
      innerOperator: 'AND',
      outerOperator: null,
    }],
  };
}

function dependOnlyOnInserted(task, newTaskId) {
  const next = { ...task, dependsOn: [newTaskId] };
  if (hasAnyConditions(task.unlockConditions)) {
    next.unlockConditions = singleDependencyOn(newTaskId);
  }
  return next;
}

function placementAbove(source, position) {
  if (position && Number.isFinite(position.x) && Number.isFinite(position.y)) {
    return {
      x: position.x,
      y: position.y,
      displayPosition: { x: position.x, y: position.y },
    };
  }
  const x = typeof source.x === 'number' ? source.x : 100;
  const y = (typeof source.y === 'number' ? source.y : 40) - BEFORE_OFFSET_Y;
  const displayPosition = source.position
    ? { x: source.position.x, y: source.position.y - BEFORE_OFFSET_Y }
    : { x, y };
  return { x, y, displayPosition };
}

/**
 * @returns {{ newTaskId: string, newTask: object, tasks: object[], displayPosition: {x: number, y: number} } | null}
 */
export function buildMissionBefore(tasks, sourceId, { newTaskId, now, nodeType = 'task', position = null }) {
  const source = (tasks || []).find((task) => task.id === sourceId);
  if (!isParallelMissionSource(source) || !newTaskId) return null;

  const { x, y, displayPosition } = placementAbove(source, position);
  const inputs = copyIncomingDependencies(source, sourceId);

  const newTask = {
    id: newTaskId,
    name: nodeType === 'wait' ? defaultWaitTitle('email') : '',
    order: (tasks?.length || 0) + 1,
    groupId: null,
    dependsOn: inputs.dependsOn,
    status: 'pending',
    x,
    y,
    createdAt: now,
    updatedAt: now,
    startedAt: null,
    completedAt: null,
    estimatedDurationMinutes: null,
    dueDate: null,
    ...(inputs.unlockConditions ? { unlockConditions: inputs.unlockConditions } : {}),
    ...(source.sourceCampaign ? { sourceCampaign: source.sourceCampaign } : {}),
    ...(source._campaignOffset != null ? { _campaignOffset: source._campaignOffset } : {}),
    ...(source.position
      ? {
          position: displayPosition,
          _originalPosition: { x, y },
        }
      : {}),
    ...(nodeType === 'decision'
      ? {
          nodeType: 'decision',
          options: [
            { id: 'yes', label: 'Yes', color: '#22c55e' },
            { id: 'no', label: 'No', color: '#ef4444' },
          ],
          answer: null,
        }
      : {}),
    ...(nodeType === 'wait'
      ? {
          nodeType: 'wait',
          waitFor: 'email',
          waitDetails: '',
          fulfilledAt: null,
        }
      : {}),
  };

  const rewired = (tasks || []).map((task) => (
    task.id === sourceId ? dependOnlyOnInserted(task, newTaskId) : task
  ));

  return {
    newTaskId,
    newTask,
    tasks: [...rewired, newTask],
    displayPosition,
  };
}
